<template>
  <div>
    <material-button
      class="d-none show-modal-connect-airwallex"
      data-bs-toggle="modal"
      data-bs-target="#connectAirwallex"
    ></material-button>
    <div
      class="modal fade"
      id="connectAirwallex"
      tabindex="-1"
      aria-hidden="true"
      style="z-index: 1056"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Connect Airwallex
            </h5>
            <i
              style="cursor: pointer"
              class="material-icons btn-close-modal"
              data-bs-dismiss="modal"
              aria-label="Close"
              >clear</i
            >
          </div>
          <div class="modal-body" style="overflow-y: unset">
            <!-- Status display when account exists -->
            <div v-if="currentBadge" class="mb-3">
              <div class="d-flex align-items-center mb-2">
                <span class="fw-bold me-2">Status:</span>
                <span
                  class="badge"
                  :class="statusBadgeClass"
                >
                  {{ badgeLabel }}
                </span>
              </div>
            </div>

            <!-- Form fields for NEW connection only (no existing account) -->
            <div v-if="!currentBadge">
              <div class="group-option-list mb-3">
                <label class="fw-bold">
                  Business Type <span class="text-danger ms-2">*</span>
                </label>
                <Multiselect
                  v-model="legal_entity_type"
                  :options="businessTypeOptions"
                  :canClear="false"
                  :disabled="true"
                />
              </div>
              <div class="group-option-list mb-3">
                <label class="fw-bold">
                  Country <span class="text-danger ms-2">*</span>
                </label>
                <Multiselect
                  v-model="country_code"
                  :options="countryOptions"
                  :canClear="false"
                  :disabled="true"
                />
              </div>
              <div class="group-option-list mb-3">
                <label class="fw-bold" for="airwallex_email">
                  Contact Email <span class="text-danger ms-2">*</span>
                </label>
                <material-input
                  v-model="email"
                  type="email"
                  id="airwallex_email"
                  name="airwallex_email"
                />
              </div>
            </div>

            <!-- Error message -->
            <div
              class="mt-2 mb-3 text-danger"
              style="word-break: break-all"
              v-if="messageErr"
            >
              {{ messageErr }}
            </div>

            <!-- What the merchant is waiting for, and what to do -->
            <div v-if="statusNotice" class="alert mb-0" :class="statusNotice.class">
              {{ statusNotice.text }}
            </div>

            <!-- Airwallex requests for information (RFIs) not closed yet -->
            <div v-if="currentBadge && rfis.length" class="mt-3">
              <div v-for="rfi in rfis" :key="rfi.rfi_id" class="rfi-card mb-3">
                <div class="d-flex align-items-center justify-content-between mb-1">
                  <span class="fw-bold">{{ rfiTypeLabel(rfi.type) }}</span>
                  <span class="badge" :class="rfi.status === 'ACTION_REQUIRED' ? 'bg-warning' : 'bg-info'">
                    {{ rfi.status === "ACTION_REQUIRED" ? "Response required" : "In review" }}
                  </span>
                </div>
                <div v-if="rfi.raised_at" class="text-sm text-muted mb-2">
                  Requested {{ formatDateTime(rfi.raised_at) }}
                </div>
                <div v-for="(question, index) in rfi.questions" :key="index" class="mb-2">
                  <div class="fw-bold text-sm">{{ question.title || "Additional information" }}</div>
                  <div v-if="question.description" class="text-sm rfi-text">
                    <template v-for="(part, partIndex) in textParts(question.description)" :key="partIndex">
                      <a
                        v-if="part.href"
                        :href="part.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-decoration-underline"
                      >{{ part.text }}</a>
                      <template v-else>{{ part.text }}</template>
                    </template>
                  </div>
                  <div v-if="question.comment" class="text-sm fst-italic rfi-text">
                    Airwallex comment:
                    <template v-for="(part, partIndex) in textParts(question.comment)" :key="partIndex">
                      <a
                        v-if="part.href"
                        :href="part.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-decoration-underline"
                      >{{ part.text }}</a>
                      <template v-else>{{ part.text }}</template>
                    </template>
                  </div>
                  <a
                    v-if="question.action_url && rfi.status === 'ACTION_REQUIRED'"
                    :href="question.action_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-sm text-decoration-underline"
                  >Open verification link</a>
                </div>
                <material-button
                  v-if="rfi.can_respond"
                  class="btn btn-sm btn-background mt-1"
                  @click="respondRfi(rfi)"
                >
                  Respond
                </material-button>
                <div
                  v-else-if="rfi.status === 'ACTION_REQUIRED'"
                  class="text-sm text-muted"
                >
                  Please send the requested information to Minty support, who will submit it to Airwallex.
                </div>
              </div>
            </div>

            <div v-if="currentBadge" class="d-flex align-items-center justify-content-between text-sm text-muted mt-3">
              <span v-if="detailsLoading">Loading details...</span>
              <span v-else-if="details && details.last_synced_at">Last checked {{ formatDateTime(details.last_synced_at) }}</span>
              <span v-else></span>
              <a
                href="#"
                class="text-decoration-underline"
                :class="{ 'pe-none opacity-5': detailsLoading }"
                @click.prevent="loadDetails(true)"
              >Refresh status</a>
            </div>

            <div v-if="!currentBadge" class="d-flex align-items-center mt-3">
              <input
                id="airwallex_agree_terms"
                v-model="agreedToTerms"
                type="checkbox"
                class="airwallex-agree-checkbox flex-shrink-0"
              />
              <label class="ms-2 mb-0" for="airwallex_agree_terms" style="cursor: pointer;">
                By clicking "Connect" I agree to Airwallex's
                <a
                  href="https://www.airwallex.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-decoration-underline"
                >Terms &amp; Policies</a>
                and
                <a
                  href="https://www.airwallex.com/global/terms/scale-user-terms-global"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-decoration-underline"
                >Connected Account User Terms</a>.
              </label>
            </div>
            <div class="d-flex justify-content-end mt-4">
              <material-button
                v-if="!currentBadge"
                class="mx-2 btn btn-sm btn-background"
                :disabled="!agreedToTerms"
                @click="connect()"
              >
                Connect
              </material-button>
              <material-button
                v-if="canResume"
                class="mx-2 btn btn-sm btn-background"
                @click="resume()"
              >
                Continue Onboarding
              </material-button>
              <material-button
                class="mx-2 btn btn-sm btn-background btn-cancel-airwallex"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                {{ !currentBadge ? "Cancel" : "Close" }}
              </material-button>
            </div>
          </div>
        </div>
      </div>
      <material-loading :active="loaderActive" />
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import MaterialInput from "@/components/MaterialInput.vue";
import PaymentService from "@/services/payment.service";
import Multiselect from "@vueform/multiselect";
import { airwallexBadgeLabel, airwallexBadgeClass } from "../airwallexBadge";

const RFI_TYPE_LABELS = {
  KYC: "Business verification request",
  KYC_ONGOING: "Ongoing verification request",
  CARDHOLDER: "Cardholder verification request",
  PAYMENT_ENABLEMENT: "Payment activation request",
  TRANSACTION: "Transaction review request",
  MERCHANT_RISK: "Merchant risk review request",
};

export default {
  name: "connect-airwallex",
  components: {
    MaterialLoading,
    MaterialButton,
    MaterialInput,
    Multiselect,
  },
  props: {
    keyReload: {
      type: String,
      required: true,
    },
    merchantId: {
      type: [Number, String],
      required: true,
    },
    merchantEmail: {
      type: String,
      default: "",
    },
    physicalBranchId: {
      type: [Number, String],
      default: 0,
    },
    airwallexBadge: {
      type: String,
      default: "",
    },
    getList: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loaderActive: false,
      messageErr: "",
      legal_entity_type: "BUSINESS",
      country_code: "SG",
      email: "",
      currentBadge: "",
      agreedToTerms: false,
      details: null,
      detailsLoading: false,
      detailsRequestId: 0,
      businessTypeOptions: [
        { value: "BUSINESS", label: "Business" },
      ],
      countryOptions: [
        { value: "SG", label: "Singapore" },
      ],
    };
  },
  computed: {
    rfis() {
      return this.details?.rfis || [];
    },
    hasOpenRfi() {
      return this.rfis.some((rfi) => rfi.status === "ACTION_REQUIRED");
    },
    // The API decides whether the onboarding form still helps; before the
    // details load, fall back to the badge.
    canResume() {
      if (this.details) return !!this.details.can_continue;
      return this.currentBadge === "KYC_NOT_SUBMITTED";
    },
    badgeLabel() {
      return airwallexBadgeLabel(this.currentBadge);
    },
    statusBadgeClass() {
      return airwallexBadgeClass(this.currentBadge);
    },
    // Every state says who we are waiting for and what, if anything, the
    // merchant has to do.
    statusNotice() {
      switch (this.currentBadge) {
        case "KYC_NOT_SUBMITTED":
          return { class: "alert-info", text: "Your application has not been submitted yet. Click \"Continue Onboarding\" to finish it on Airwallex." };
        case "KYC_IN_REVIEW":
          return { class: "alert-info", text: "Airwallex is reviewing your application. Nothing is needed from you right now; this usually takes a few hours to a few days." };
        case "INFORMATION_REQUIRED":
          return this.canResume
            ? { class: "alert-warning", text: "Airwallex needs more information. Click \"Continue Onboarding\" to update your application." }
            : { class: "alert-warning", text: "Airwallex needs more information from you. Please see the request below." };
        case "ACTIVE":
          return this.hasOpenRfi
            ? { class: "alert-warning", text: "Your Airwallex account is active, but Airwallex has requested more information. Please see the request below." }
            : { class: "alert-success", text: "Your Airwallex account is active and ready to use." };
        case "FROZEN":
          return { class: "alert-danger", text: "Your Airwallex account is temporarily frozen and cannot take payments. Please contact Minty support." };
        case "REJECTED":
          return { class: "alert-danger", text: "Airwallex could not approve this account. Please contact Minty support." };
        case "DORMANT":
          return { class: "alert-info", text: "This Airwallex account is dormant." };
        case "CLOSED":
          return { class: "alert-info", text: "This Airwallex account is closed." };
        default:
          return null;
      }
    },
  },
  methods: {
    connect() {
      if (!this.email) {
        this.messageErr = "Contact Email is required";
        return;
      }
      if (!this.agreedToTerms) {
        this.messageErr =
          "You must agree to the Airwallex Terms & Policies and Connected Account User Terms before connecting.";
        return;
      }
      this.messageErr = "";
      this.showLoader();
      let dataForm = {
        merchant_id: this.merchantId,
        physical_branch_id: this.physicalBranchId || undefined,
        legal_entity_type: this.legal_entity_type,
        country_code: this.country_code,
        email: this.email,
        agreed_to_terms: true,
      };
      PaymentService.airwallexGetOnboardingLink(dataForm).then(
        (response) => this.handleOnboardingLink(response),
        (error) => {
          this.messageErr =
            error.response?.data?.result?.message || "An error occurred";
          this.hideLoader();
        }
      );
    },
    resume() {
      this.messageErr = "";
      this.showLoader();
      let dataForm = {
        merchant_id: this.merchantId,
        physical_branch_id: this.physicalBranchId || undefined,
      };
      PaymentService.airwallexResumeOnboarding(dataForm).then(
        (response) => this.handleOnboardingLink(response),
        (error) => {
          this.messageErr =
            error.response?.data?.result?.message || "An error occurred";
          this.hideLoader();
        }
      );
    },
    // Both Connect and Continue answer with a link, or — when there is
    // nothing left to fill in on the onboarding form — with the account's
    // current badge.
    handleOnboardingLink(response) {
      if (response.data.result.isSuccess) {
        const data = response.data.data;
        if (data.authorized_url) {
          window.location.href = data.authorized_url;
          return;
        }
        this.currentBadge = data.badge || this.currentBadge;
        this.loadDetails(false);
        this.getList(1);
      } else {
        this.messageErr = response.data.result.message;
      }
      this.hideLoader();
    },
    loadDetails(refresh = false) {
      if (!this.currentBadge) return;
      this.detailsLoading = true;
      // The modal is reused for the merchant and every branch: drop a reply
      // that arrives after it was reopened for another account.
      const requestId = ++this.detailsRequestId;
      PaymentService.airwallexOnboardingStatus({
        merchant_id: this.merchantId,
        physical_branch_id: this.physicalBranchId || undefined,
        refresh: refresh ? 1 : 0,
      }).then(
        (response) => {
          if (requestId !== this.detailsRequestId) return;
          if (response.data.result.isSuccess) {
            this.details = response.data.data;
            if (this.details.badge && this.details.badge !== this.currentBadge) {
              this.currentBadge = this.details.badge;
              this.getList(1);
            }
          } else {
            this.messageErr = response.data.result.message;
          }
          this.detailsLoading = false;
        },
        (error) => {
          if (requestId !== this.detailsRequestId) return;
          this.messageErr =
            error.response?.data?.result?.message || "Could not load the Airwallex status";
          this.detailsLoading = false;
        }
      );
    },
    respondRfi(rfi) {
      this.messageErr = "";
      this.showLoader();
      PaymentService.airwallexRespondRfi({
        merchant_id: this.merchantId,
        physical_branch_id: this.physicalBranchId || undefined,
        rfi_id: rfi.rfi_id,
      }).then(
        (response) => {
          if (response.data.result.isSuccess && response.data.data.authorized_url) {
            window.location.href = response.data.data.authorized_url;
            return;
          }
          this.messageErr = response.data.result.message || "Could not open the Airwallex page, please try again";
          this.hideLoader();
        },
        (error) => {
          this.messageErr =
            error.response?.data?.result?.message || "Could not open the Airwallex page, please try again";
          this.hideLoader();
        }
      );
    },
    // Airwallex writes RFI text in Markdown: [label](url) links and bare URLs
    // become links, everything else stays plain text (no v-html), with line
    // breaks kept by the .rfi-text style. Only http(s) links are made.
    textParts(text) {
      const parts = [];
      const pattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)\]]+)/g;
      let last = 0;
      let match;
      while ((match = pattern.exec(text)) !== null) {
        if (match.index > last) {
          parts.push({ text: text.slice(last, match.index) });
        }
        last = pattern.lastIndex;
        if (match[2]) {
          parts.push({ text: match[1], href: match[2] });
          continue;
        }
        // A bare URL at the end of a sentence keeps its full stop as text.
        const href = match[3].replace(/[.,;:!?]+$/, "");
        last -= match[3].length - href.length;
        parts.push({ text: href, href });
      }
      if (last < text.length) {
        parts.push({ text: text.slice(last) });
      }
      return parts;
    },
    // "2026-10-01 12:03:19" (API, app timezone) -> "01/10/2026 12:03", the
    // format the Payments list uses.
    formatDateTime(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value || "");
      return match ? `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}` : value;
    },
    rfiTypeLabel(type) {
      return RFI_TYPE_LABELS[type] || "Request for information";
    },
    showLoader() {
      this.loaderActive = true;
    },
    hideLoader() {
      this.loaderActive = false;
    },
  },
  watch: {
    keyReload() {
      this.messageErr = "";
      this.email = this.merchantEmail;
      this.currentBadge = this.airwallexBadge;
      this.agreedToTerms = false;
      this.details = null;
      this.detailsRequestId++;
      this.detailsLoading = false;
      // Opening the modal re-reads the account (the API allows it once a
      // minute), so what the merchant sees is never older than that.
      this.loadDetails(true);
    },
  },
};
</script>

<style scoped>
.bg-success,
.alert-success {
  background: #198754 !important;
  background-image: none !important;
  color: #fff !important;
}

.bg-primary {
  background: #0d6efd !important;
  background-image: none !important;
  color: #fff !important;
}

.bg-info,
.alert-info {
  background: #0dcaf0 !important;
  background-image: none !important;
  color: #000 !important;
}

.bg-warning,
.alert-warning {
  background: #ffc107 !important;
  background-image: none !important;
  color: #000 !important;
}

.bg-danger,
.alert-danger {
  background: #dc3545 !important;
  background-image: none !important;
  color: #fff !important;
}

.bg-secondary {
  background: #6c757d !important;
  background-image: none !important;
  color: #fff !important;
}

.airwallex-agree-checkbox {
  appearance: auto;
  -webkit-appearance: checkbox;
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  opacity: 1;
  position: static;
}
.rfi-text {
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.rfi-card {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 10px 12px;
}
</style>
